import { v4 as uuidv4 } from 'uuid';

import { openSlotLayoutBuilderAction } from 'editor/src/store/app/slice';
import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { SlotLayoutDraft } from 'editor/src/store/design/types';
import getContentAreaFromSpread from 'editor/src/store/design/util/getContentAreaFromSpread';
import defaultSlotRows from 'editor/src/store/design/util/slotLayout/defaultSlotRows';
import getCurrentSpreadIndex from 'editor/src/store/editor/selector/getCurrentSpreadIndex';
import setSidebarActiveTabByNameOperation from 'editor/src/store/editorModules/sidebar/operation/setSidebarActiveTabByNameOperation';
import { TAB_NAMES } from 'editor/src/store/editorModules/sidebar/types';
import type { Thunk } from 'editor/src/store/hooks';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

export const DEFAULT_MAX_PRODUCTS = 6;
export const DEFAULT_SLOT_GAP = 5;

/**
 * Opens the builder: on an existing layout, with its own values; otherwise with a draft that
 * already fills the page's content area with the retailer leaflet grid, so the author only
 * has to name the picklist and pick folders.
 */
const openSlotLayoutBuilderOperation =
  (layoutId?: string): Thunk =>
  (dispatch, getState, { i18n }) => {
    const state = getState();
    const spreadIndex = getCurrentSpreadIndex(state);
    const spread = state.design.designData?.spreads[spreadIndex];
    if (!spread) {
      return;
    }

    const existing = layoutId ? spread.slotLayouts?.find((layout) => layout.id === layoutId) : undefined;
    let draft: SlotLayoutDraft;
    if (existing) {
      const { name, productLabel, maxProducts, rows, area, gap, fileSets, productOptions } = existing;
      draft = { name, productLabel, maxProducts, rows, area, gap, fileSets, productOptions };
      // Folders load on demand, and a grid edit fills new frames from them: ask for the ones
      // the design reopened without, so the form can wait for them.
      fileSets
        .flatMap((fileSet) => Object.values(fileSet.folders))
        .map((folder) => folder.folderId)
        .filter((folderId, index, ids) => !state.collections.folders[folderId] && ids.indexOf(folderId) === index)
        .forEach((folderId) => sendPostMessage('advancedPersonalisation.getFolderContent', folderId));
    } else {
      const content = getContentAreaFromSpread(spread);
      const page = spread.pages[0];
      draft = {
        name: i18n.t('editor-slot-layout-default-name', 'Number of products'),
        productLabel: i18n.t('editor-slot-layout-default-product', 'Product'),
        maxProducts: DEFAULT_MAX_PRODUCTS,
        rows: defaultSlotRows(DEFAULT_MAX_PRODUCTS),
        area: content
          ? { x: content.left, y: content.top, width: content.width, height: content.height }
          : { x: page.x, y: page.y, width: page.width, height: page.height },
        gap: DEFAULT_SLOT_GAP,
        fileSets: [{ id: uuidv4(), name: i18n.t('editor-slot-layout-default-pictures', 'Pictures'), folders: {} }],
        productOptions: [],
      };
    }

    batch(() => {
      dispatch(openSlotLayoutBuilderAction({ spreadIndex, layoutId: existing?.id, draft }));
      dispatch(setSidebarActiveTabByNameOperation(TAB_NAMES.PERSONALIZE));
    });
  };

export default openSlotLayoutBuilderOperation;
